import { Baby, Clock, Microscope, Search, ShieldCheck } from "lucide-react";
import { BlogPost, QuestionOption, ResultType } from "./types";

// --- Configuration ---
export const MEDIUM_RSS_URL = "https://medium.com/feed/@babyskinq";
export const RSS2JSON_API = `https://api.rss2json.com/v1/api.json?rss_url=${encodeURIComponent(
  MEDIUM_RSS_URL
)}`;

// --- Initial Data (Fallback) ---
// Note: Blog images are set to placeholders until you upload files named blog-newborn.jpg etc.
export const INITIAL_BLOG_POSTS: BlogPost[] = [
  {
    id: "demo-1",
    title: "Why Newborn Skin Peels: A Complete Guide",
    excerpt:
      "Newborn skin peeling is common and usually harmless. Learn why it happens and how to gently care for it.",
    date: "2026-01-12",
    author: "Dr. Emily Chen",
    content:
      "<p>It can be surprising to see your newborn's skin peeling in the first few weeks of life. However, this is a completely natural process.</p><p>Generally, no treatment is necessary. However, if the skin seems dry or cracked, use gentle moisturizers.</p>",
    image: "https://picsum.photos/800/600?random=1",
    link: "#",
  },
  {
    id: "demo-2",
    title: "Understanding Eczema Triggers in Toddlers",
    excerpt:
      "Eczema flare-ups can be frustrating. Identifying these common triggers is the first step to management.",
    date: "2025-12-28",
    author: "Sarah Johnson",
    content:
      "<p>Eczema (atopic dermatitis) affects up to 20% of children. Common triggers include dry skin, irritants, heat, and allergens.</p>",
    image: "https://picsum.photos/800/600?random=2",
    link: "#",
  },
  {
    id: "demo-3",
    title: "The Importance of pH Balance",
    excerpt:
      "Why 'pH-balanced' isn't just a marketing buzzword when it comes to your baby's delicate skin barrier.",
    date: "2025-11-15",
    author: "Abooba Research",
    content:
      "<p>Healthy skin has a thin, protective layer on its surface, referred to as the acid mantle. This layer is slightly acidic, with a pH around 5.5.</p>",
    image: "https://picsum.photos/800/600?random=3",
    link: "#",
  },
];

// --- Data Structure for Quiz ---
export const QUESTIONS: QuestionOption[] = [
  {
    id: "age",
    question: "How old is your baby?",
    sub: "Age helps us recommend the gentlest fit.",
    icon: Baby,
    options: ["0–6 months", "7–24 months", "2–4 years", "5+ years"],
  },
  {
    id: "concern",
    question: "What’s the #1 skin concern right now?",
    sub: "Select the primary symptom observed.",
    icon: Search,
    options: [
      "Dryness / flaking",
      "Redness / irritation",
      "Itchiness / scratching",
      "Occasional bumps",
      "No major issues (prevention)",
    ],
  },
  {
    id: "worse",
    question: "When does it get worse?",
    sub: "Identify the triggering environment.",
    icon: Clock,
    options: [
      "After bath time",
      "In dry weather",
      "At night",
      "After being outside",
      "Not sure",
    ],
  },
  {
    id: "current",
    question: "What are you using today?",
    sub: "Current regimen analysis.",
    icon: Microscope,
    options: [
      "Baby lotion / cream",
      "Adult skincare product",
      "Pharmacy / medicated cream",
      "Nothing yet",
    ],
  },
  {
    id: "value",
    question: "What matters most to you?",
    sub: "Priority filter for recommendations.",
    icon: ShieldCheck,
    options: [
      "Ingredient safety",
      "Long-lasting moisture",
      "Soothing effect",
      "Dermatologist-tested",
      "Budget-friendly",
    ],
  },
];

export const RESULTS: Record<string, ResultType> = {
  A: {
    type: "TYPE A",
    title: "Extremely Sensitive / Newborn Skin",
    headline: "Your baby’s skin needs ultra-gentle care.",
    summary: [
      "Very delicate skin can react quickly.",
      "Comfort-first, fragrance-free hydration works best.",
      "Look for clinically tested, newborn-friendly formulas.",
    ],
    products: [
      {
        brand: "Mustela",
        name: "Stelatopia Emollient Cream",
        bestFor: "Eczema-prone, very dry skin",
        size: "400 ml / 1.35 oz",
        price: "$17.00",
        isHero: true,
        image: "/images/mustela-stelatopia.jpg",
        features: [
          "Fragrance-free",
          "Plant-based oils",
          "Barrier-repair formula",
        ],
        link: "https://amzn.to/3Zponex",
        ugc: "“Helped calm my newborn’s dry patches in just a few days.”",
        ugcAuthor: "Sarah, CA",
      },
      {
        brand: "Aveeno Baby",
        name: "Eczema Therapy",
        bestFor: "Itchy, eczema-prone skin",
        size: "7.3 oz / 206 g",
        price: "$18.98",
        image: "/images/aveeno-eczema.jpg",
        features: ["Colloidal oatmeal", "Steroid-free", "Pediatrician tested"],
        link: "https://amzn.to/4qtfaxP",
        ugc: "“My baby stopped scratching after one week.”",
        ugcAuthor: "Jessica, TX",
      },
    ],
  },
  B: {
    type: "TYPE B",
    title: "Dry / Flaky Skin",
    headline: "Your baby needs long-lasting moisture + barrier support.",
    summary: [
      "Dry skin means moisture isn’t staying locked in.",
      "Barrier support helps keep hydration longer.",
      "Choose a cream that works day and night.",
    ],
    products: [
      {
        brand: "CeraVe Baby",
        name: "Moisturizing Cream",
        bestFor: "Dry, sensitive skin",
        size: "8 oz / 226 g",
        price: "$12.49",
        isHero: true,
        image: "/images/cerave-cream.jpg",
        features: ["3 Ceramides", "Hyaluronic acid", "Fragrance-free"],
        link: "https://amzn.to/49EreVC",
        ugc: "“Keeps my baby’s skin soft all day.”",
        ugcAuthor: "Amanda, FL",
      },
      {
        brand: "Eucerin Baby",
        name: "Eczema Relief",
        bestFor: "Eczema & itch relief",
        size: "8 oz / 226 g",
        price: "$17.69",
        image: "/images/eucerin-eczema.jpg",
        features: ["Colloidal oatmeal", "Ceramide-3", "Fragrance-free"],
        link: "https://amzn.to/4t7MyvU",
        ugc: "“Rich texture that really heals the dryness.”",
        ugcAuthor: "Mike, NY",
      },
    ],
  },
  C: {
    type: "TYPE C",
    title: "Redness / Irritation",
    headline: "Your baby’s skin needs soothing, irritation-friendly care.",
    summary: [
      "Red skin needs immediate calming ingredients.",
      "Avoid any harsh chemicals or fragrances.",
      "Focus on repairing the skin barrier gently.",
    ],
    products: [
      {
        brand: "La Roche-Posay",
        name: "Lipikar Balm AP+",
        bestFor: "Red, irritated skin",
        size: "13.52 oz / 400 ml",
        price: "$21.99",
        isHero: true,
        image: "/images/lrp-lipikar.jpg",
        features: ["Prebiotic thermal water", "Shea butter", "Niacinamide"],
        link: "https://amzn.to/4jOIFHI",
        ugc: "“Redness was visibly calmer after a few uses.”",
        ugcAuthor: "Nicole, WA",
      },
      {
        brand: "Tubby Todd",
        name: "All Over Ointment",
        bestFor: "Irritation & redness",
        size: "8 oz / 227 g",
        price: "$71.98",
        image: "/images/tubby-todd-ointment.jpg",
        features: ["Plant-based", "Multi-purpose", "Safe for newborns"],
        link: "https://amzn.to/4jPPM2V",
        ugc: "“A miracle for sensitive patches.”",
        ugcAuthor: "Jamie, OR",
      },
      {
        brand: "Aquaphor",
        name: "Baby Healing Ointment",
        bestFor: "Extra-dry, cracked skin",
        size: "7 oz / 198 g",
        price: "$13.59",
        image: "/images/aquaphor-baby.jpg",
        features: ["Protective ointment", "Multi-purpose", "Fragrance-free"],
        link: "https://amzn.to/4bHiz7x",
        ugc: "“The only thing that stopped the red rashes.”",
        ugcAuthor: "Megan, OH",
      },
    ],
  },
  D: {
    type: "TYPE D",
    title: "Active Baby / Daily Protection",
    headline: "Your baby needs daily protection for active days.",
    summary: [
      "Active babies need lightweight protection.",
      "Sweat and outdoors can dry skin quickly.",
      "Use a non-greasy formula for day use.",
    ],
    products: [
      {
        brand: "Baby Dove",
        name: "Sensitive Lotion",
        bestFor: "Sensitive baby skin",
        size: "20 oz / 591 ml",
        price: "$9.99",
        isHero: true,
        image: "/images/dove-sensitive.jpg",
        features: ["Fragrance-free", "Hypoallergenic", "Moisture-protecting"],
        link: "https://amzn.to/4jOTUjw",
        ugc: "“Perfect for daycare days and outdoor play.”",
        ugcAuthor: "Laura, CA",
      },
      {
        brand: "JOHNSON'S BABY",
        name: "CottonTouch Newborn Face & Body Lotion",
        bestFor: "Newborns & Hot/Humid Weather",
        size: "13.6 oz / 400ml",
        price: "$5.47",
        image: "/images/johnsons-cottontouch.jpg",
        features: ["Real Cotton Infused", "Ultra-Light Formula", "ph Balanced"],
        link: "https://amzn.to/4k4Ykmv",
        ugc: "“Absorbs quickly without greasiness. Perfect for hot summer days.”",
        ugcAuthor: "Verified Purchaser",
      },
      {
        brand: "CETAPHIL",
        name: "Baby Daily Lotion (Organic Calendula)",
        bestFor: "Active Skin needing Soothing & Hydration",
        size: "13.5 oz / 399ml",
        price: "$9.97",
        image: "/images/cetaphil-calendula.jpg",
        features: ["Organic Calendula", "Plant Oil Blend", "Hypoallergenic"],
        link: "https://amzn.to/3LJ8A7m",
        ugc: "“Leaves no residue and smells delicious. Keeps skin soft all day.”",
        ugcAuthor: "Amazon Customer",
      },
    ],
  },
  E: {
    type: "TYPE E",
    title: "Preventive Care",
    headline:
      "Prevention is your superpower: build a strong skin barrier daily.",
    summary: [
      "Consistent moisture prevents future flare-ups.",
      "Maintenance is easier than treatment.",
      "Keep the skin barrier strong every day.",
    ],
    products: [
      {
        brand: "Pipette",
        name: "Baby Lotion",
        bestFor: "Daily hydration",
        size: "5.7 oz / 170 ml",
        price: "$17.87",
        isHero: true,
        image: "/images/pipette-lotion.jpg",
        features: ["Plant-derived squalane", "Clean & vegan", "Fragrance-free"],
        link: "https://amzn.to/4pVg2tP",
        ugc: "“Lightweight but keeps skin soft all day.”",
        ugcAuthor: "Hannah, IL",
      },
      {
        brand: "CALIFORNIA BABY",
        name: "Super Sensitive Everyday Lotion",
        bestFor: "Ultra-Sensitive / Chemical Intolerant Skin",
        size: "8.5 oz / 251ml",
        price: "$21.99",
        image: "/images/california-baby.jpg",
        features: [
          "100% Plant-Based",
          "Strictly Allergy Tested",
          "No Fragrance",
        ],
        link: "https://amzn.to/3Zs3Ypf",
        ugc: "“Worth every penny. The only lotion that doesn’t irritate my baby’s eczema.”",
        ugcAuthor: "Sensitive Skin Mom",
      },
      {
        brand: "THE HONEST COMPANY",
        name: "Purely Sensitive Face & Body Lotion",
        bestFor: "Daily Maintenance with Clean Ingredients",
        size: "8.5 oz / 250ml",
        price: "$11.99",
        image: "/images/honest-lotion.jpg",
        features: ["Nourishing Blend", "Clean Conscious", "Fast Absorbing"],
        link: "https://amzn.to/4pUhqx6",
        ugc: "“Gentle, not greasy, and works well. I love that it’s fragrance-free.”",
        ugcAuthor: "Honest Shopper",
      },
    ],
  },
};
